"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import {
  type KeyedOutboundRedirectRequest,
  type OutboundRedirectRequest,
  type RedirectMode,
  type RedirectResourceType,
  getBatchOutboundRedirectUrls,
  getOutboundRedirectUrl,
} from "@/lib/outbound-redirect";

interface UseOutboundRedirectUrlOptions {
  resourceType: RedirectResourceType;
  resourceId?: string | null;
  merchant?: string | null;
  catalog?: string | null;
  redirectMode?: RedirectMode;
}

interface UseOutboundRedirectUrlResult {
  redirectUrl: string;
  isLoading: boolean;
}

export function useOutboundRedirectUrl(
  destinationUrl: string | null | undefined,
  options: UseOutboundRedirectUrlOptions
): UseOutboundRedirectUrlResult {
  const [redirectUrl, setRedirectUrl] = useState("");
  const [resolvedDestinationUrl, setResolvedDestinationUrl] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const { resourceType, resourceId, merchant, catalog, redirectMode } = options;

  useEffect(() => {
    if (!destinationUrl) {
      setRedirectUrl("");
      setResolvedDestinationUrl(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setResolvedDestinationUrl(null);

    const request: OutboundRedirectRequest = {
      destinationUrl,
      resourceType,
      resourceId: resourceId ?? undefined,
      merchant: merchant ?? undefined,
      catalog: catalog ?? undefined,
      redirectMode: redirectMode ?? "temporary",
    };

    getOutboundRedirectUrl(request)
      .then((url) => {
        if (currentRequestId === requestIdRef.current) {
          setRedirectUrl(url);
          setResolvedDestinationUrl(destinationUrl);
        }
      })
      .catch(() => {
        if (currentRequestId === requestIdRef.current) {
          setRedirectUrl("");
          setResolvedDestinationUrl(destinationUrl);
        }
      });
  }, [destinationUrl, resourceType, resourceId, merchant, catalog, redirectMode]);

  const isLoading = Boolean(destinationUrl) && resolvedDestinationUrl !== destinationUrl;

  return { redirectUrl, isLoading };
}

interface UseBatchOutboundRedirectUrlsResult {
  redirectUrls: Record<string, string>;
  isLoading: boolean;
}

export function useBatchOutboundRedirectUrls(
  requests: KeyedOutboundRedirectRequest[]
): UseBatchOutboundRedirectUrlsResult {
  const [redirectUrls, setRedirectUrls] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const stableKey = useMemo(
    () => requests.map((r) => `${r.key}:${r.destinationUrl}`).join("|"),
    [requests]
  );

  useEffect(() => {
    if (requests.length === 0) {
      setRedirectUrls({});
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);

    getBatchOutboundRedirectUrls(requests).then((urls) => {
      if (currentRequestId === requestIdRef.current) {
        setRedirectUrls(urls);
        setIsLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  return { redirectUrls, isLoading };
}
